// Shared LLM vendor/model catalog used across agent UIs.

export const LLM_VENDORS = [
    { value: 'OpenAI', label: 'OpenAI' },
    { value: 'Google', label: 'Google' },
    { value: 'Anthropic', label: 'Anthropic' },
    { value: 'Bedrock', label: 'Bedrock' },
];

export const LLM_MODELS_BY_VENDOR = {
    OpenAI: [
        { value: 'gpt-5', label: 'gpt-5 — flagship reasoning + multimodal' },
        { value: 'gpt-5-mini', label: 'gpt-5-mini — faster, cheaper' },
        { value: 'gpt-5-nano', label: 'gpt-5-nano — smallest, fastest' },
        { value: 'gpt-4.1', label: 'gpt-4.1 — strong general-purpose + multimodal' },
        { value: 'gpt-4o', label: 'gpt-4o — strong general-purpose + multimodal' },
    ],
    Google: [
        { value: 'gemini-2.0-pro', label: 'gemini-2.0-pro — advanced multimodal' },
        { value: 'gemini-1.5-pro', label: 'gemini-1.5-pro — advanced multimodal' },
        { value: 'gemini-1.5-flash', label: 'gemini-1.5-flash — fast, cost-efficient' },
    ],
    Anthropic: [
        // Older explicit versions (kept for backwards compatibility)
        { value: 'claude-3-5-sonnet-20240620', label: 'claude-3-5-sonnet-20240620 — balanced reasoning + coding' },
        { value: 'claude-3-opus-20240229', label: 'claude-3-opus-20240229 — high-end for complex tasks' },
        { value: 'claude-3-haiku-20240307', label: 'claude-3-haiku-20240307 — fast, lightweight' },

        // Newer simplified names (also kept to match existing data)
        { value: 'claude-sonnet-4-5', label: 'claude-sonnet-4-5 — balanced reasoning + coding' },
        { value: 'claude-opus-4-6', label: 'claude-opus-4-6 — high-end for complex tasks' },
        { value: 'claude-haiku-4-5', label: 'claude-haiku-4-5 — fast, lightweight' },
    ],
    Bedrock: [
        { value: 'jp.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5' },
        // { value: 'qwen.qwen3-32b-v1:0', label: 'Qwen3 32B — Tokyo, fast Japanese RAG' },
        // { value: 'jp.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5' },
        // { value: 'jp.anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude Opus 4.5' },
        { value: 'jp.anthropic.claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
        { value: 'jp.anthropic.claude-opus-5-5', label: 'Claude Opus 5.5' },
        { value: 'jp.anthropic.claude-opus-4-8', label: 'Claude Opus 4.8' },
    ],
};

export const REASONING_EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'];

const CLAUDE_EFFORT_LEVELS = {
    'claude-opus-5-5': REASONING_EFFORT_LEVELS,
    'claude-opus-4-8': REASONING_EFFORT_LEVELS,
    'claude-opus-4-6': ['low', 'medium', 'high', 'max'],
    'claude-sonnet-4-6': ['low', 'medium', 'high', 'max'],
    'claude-opus-4-5': ['low', 'medium', 'high'],
};

export function getReasoningEffortLevels(vendor, model) {
    if (!['Bedrock', 'Anthropic'].includes(vendor)) return [];
    const baseModel = String(model || '').split('anthropic.').pop();
    const key = Object.keys(CLAUDE_EFFORT_LEVELS).find((name) => (
        baseModel === name || baseModel.startsWith(`${name}-`)
    ));
    return CLAUDE_EFFORT_LEVELS[key] || [];
}

export function supportsReasoningEffort(vendor, model) {
    return getReasoningEffortLevels(vendor, model).length > 0;
}

export function getLlmModelOptions(vendor) {
    const key = String(vendor ?? '').trim();
    return LLM_MODELS_BY_VENDOR[key] || [];
}

export function normalizeLlmVendor(vendor) {
    const raw = String(vendor ?? '').trim();
    if (!raw) return '';
    const lower = raw.toLowerCase();

    // Common backend aliases / human-readable variants.
    const aliasMap = {
        openai: 'OpenAI',
        'open-ai': 'OpenAI',
        open_ai: 'OpenAI',
        'open ai': 'OpenAI',

        google: 'Google',
        gemini: 'Google',
        vertex: 'Google',
        'vertexai': 'Google',
        'vertex-ai': 'Google',
        'google ai': 'Google',

        anthropic: 'Anthropic',
        claude: 'Anthropic',

        bedrock: 'Bedrock',
        'aws-bedrock': 'Bedrock',
        'aws bedrock': 'Bedrock',
        'amazon bedrock': 'Bedrock',
        awsbedrock: 'Bedrock',
    };

    if (aliasMap[lower]) return aliasMap[lower];

    const match = (Array.isArray(LLM_VENDORS) ? LLM_VENDORS : []).find((v) => {
        const value = String(v?.value ?? '').trim();
        const label = String(v?.label ?? '').trim();
        return value.toLowerCase() === lower || label.toLowerCase() === lower;
    });
    return match?.value || raw;
}

export function isKnownLlmVendor(vendor) {
    const v = String(vendor ?? '').trim();
    if (!v) return false;
    return (Array.isArray(LLM_VENDORS) ? LLM_VENDORS : []).some((x) => String(x?.value ?? '').trim() === v);
}

export function inferVendorFromModel(model) {
    const raw = String(model ?? '').trim();
    if (!raw) return '';

    for (const [vendor, models] of Object.entries(LLM_MODELS_BY_VENDOR || {})) {
        const list = Array.isArray(models) ? models : [];
        if (list.some((m) => String(m?.value ?? '').trim() === raw)) {
            return vendor;
        }
    }

    return '';
}
